import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  ContractDetailsWrapper,
  ContractDetailsHeader,
  ContractDetailsContent,
  ContractTitleRow,
  ContractDetailsTitle,
  ContractTypeBadge,
  ActionsRow,
  ContractInfo,
  InfoField,
  InfoLabel,
  InfoValue,
  ClausesSection,
  ClauseHeading,
  ClauseParagraph,
} from "./Styles";
import { StatusBadge } from "../ContractsList/Styles";
import { IoIosArrowRoundBack } from "react-icons/io";
import { FiDownload, FiEdit2, FiArchive, FiRefreshCw } from "react-icons/fi";
import { Button } from "../../../Components/Button";
import { Modal } from "../../../Components/Modal";
import { ProcessingMessage } from "../../../Components/ProcessingMessage";
import { useContracts } from "../../../Common/Hooks/useContracts";
import { Contract, CONTRACT_STATUS_ACTIVE, CONTRACT_STATUS_ARCHIVED } from "../../../Common/Types-DTOs/Contracts";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import { formatAbbreviatedDate } from "../../../Common/Utils/dateUtils";
import { ContractVariablesPanel } from "../ContractVariablesPanel";
import { EditContractModal } from "./EditContractModal";

const PENDING_DATA_PATTERN = /\[Pendiente de rellenar por el usuario: [^\]]+\]/;

const renderClauseParagraph = (text: string) => {
  const parts = text.split(/(\[Pendiente de rellenar por el usuario: [^\]]+\])/g);
  return parts.map((part, index) => (PENDING_DATA_PATTERN.test(part) ? <strong key={index}>{part}</strong> : part));
};

export const ContractDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const { getContract, downloadDocx, updateContractMetadata } = useContracts();
  const [contract, setContract] = useState<Contract | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMissingVarsConfirm, setShowMissingVarsConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const loadPromiseRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    if (!contractId || loadPromiseRef.current) return;

    const load = async () => {
      setIsLoading(true);
      const result = await getContract(contractId);
      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("contracts.contractDetail.notifications.loadFailed"))
        );
        setIsLoading(false);
        return;
      }
      setContract(result.value.contract);
      setIsLoading(false);
    };

    loadPromiseRef.current = load();
  }, [contractId, t]);

  useEffect(() => {
    if (!contractId) return;

    const onContractVariablesUpdated = async (payload: { contractId: string }) => {
      if (payload.contractId !== contractId) return;
      const result = await getContract(contractId);
      if (!isErr(result)) setContract(result.value.contract);
    };

    eventBus.on("CONTRACT_VARIABLES_UPDATED", onContractVariablesUpdated);
    return () => {
      eventBus.off("CONTRACT_VARIABLES_UPDATED", onContractVariablesUpdated);
    };
  }, [contractId]);

  const missingRequiredVariables = contract
    ? contract.variableSchema.filter((variable) => variable.required && !contract.variables[variable.key]?.trim())
    : [];

  const runDownload = async () => {
    if (!contractId) return;
    setIsDownloading(true);
    const result = await downloadDocx(contractId, `contrato-${contractId}.docx`);
    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("contracts.contractDetail.notifications.downloadFailed"))
      );
    }
    setIsDownloading(false);
  };

  const handleDownload = async () => {
    if (missingRequiredVariables.length > 0) {
      setShowMissingVarsConfirm(true);
      return;
    }
    await runDownload();
  };

  const handleArchiveToggle = async () => {
    if (!contract) return;
    const nextStatus = contract.status === CONTRACT_STATUS_ARCHIVED ? CONTRACT_STATUS_ACTIVE : CONTRACT_STATUS_ARCHIVED;
    const result = await updateContractMetadata(contract.id, { status: nextStatus });
    setShowArchiveConfirm(false);

    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          nextStatus === CONTRACT_STATUS_ARCHIVED
            ? t("contracts.contractDetail.notifications.archiveFailed")
            : t("contracts.contractDetail.notifications.restoreFailed")
        )
      );
      return;
    }

    setContract(result.value.contract);
    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification(
        "success",
        nextStatus === CONTRACT_STATUS_ARCHIVED
          ? t("contracts.contractDetail.notifications.archiveSuccess")
          : t("contracts.contractDetail.notifications.restoreSuccess")
      )
    );
  };

  if (isLoading) {
    return (
      <ContractDetailsWrapper>
        <ContractDetailsHeader onClick={() => navigate("/contracts")}>
          <IoIosArrowRoundBack />
          <span>{t("contracts.contractDetail.backButton")}</span>
        </ContractDetailsHeader>
        <ContractDetailsContent>
          <ProcessingMessage text={t("contracts.contractDetail.loadingContract")} />
        </ContractDetailsContent>
      </ContractDetailsWrapper>
    );
  }

  if (!contract) {
    return (
      <ContractDetailsWrapper>
        <ContractDetailsHeader onClick={() => navigate("/contracts")}>
          <IoIosArrowRoundBack />
          <span>{t("contracts.contractDetail.backButton")}</span>
        </ContractDetailsHeader>
        <ContractDetailsContent>
          <p>{t("contracts.contractDetail.notFound")}</p>
        </ContractDetailsContent>
      </ContractDetailsWrapper>
    );
  }

  return (
    <ContractDetailsWrapper>
      <ContractVariablesPanel contract={contract} onSaved={setContract} />

      <ContractDetailsHeader onClick={() => navigate("/contracts")}>
        <IoIosArrowRoundBack />
        <span>{t("contracts.contractDetail.backButton")}</span>
      </ContractDetailsHeader>
      <ContractDetailsContent>
        <ContractTitleRow>
          <ContractDetailsTitle>{contract.title}</ContractDetailsTitle>
          <ContractTypeBadge>{contract.templateName}</ContractTypeBadge>
          {contract.status === CONTRACT_STATUS_ARCHIVED && <StatusBadge $status={contract.status}>{t("contracts.contractDetail.archivedBadge")}</StatusBadge>}
        </ContractTitleRow>

        <ContractInfo>
          <InfoField>
            <InfoLabel>{t("contracts.contractDetail.dateLabel")}</InfoLabel>
            <InfoValue>{formatAbbreviatedDate(contract.createdAt)}</InfoValue>
          </InfoField>
        </ContractInfo>

        <ActionsRow>
          <Button onClick={handleDownload} isLoading={isDownloading}>
            <FiDownload /> {t("contracts.contractDetail.downloadButton")}
          </Button>
          <Button onClick={() => setShowEditModal(true)} style={{ backgroundColor: '#ffffff', color: '#111827', border: '1px solid #E5E7EB' }}>
            <FiEdit2 /> {t("contracts.contractDetail.editButton")}
          </Button>
          <Button
            onClick={() => setShowArchiveConfirm(true)}
            style={{ backgroundColor: '#ffffff', color: '#111827', border: '1px solid #E5E7EB' }}
          >
            {contract.status === CONTRACT_STATUS_ARCHIVED ? <FiRefreshCw /> : <FiArchive />}
            {contract.status === CONTRACT_STATUS_ARCHIVED
              ? t("contracts.contractDetail.restoreButton")
              : t("contracts.contractDetail.archiveButton")}
          </Button>
        </ActionsRow>

        <ClausesSection>
          {contract.sections.map((section, index) => (
            <div key={index}>
              <ClauseHeading>{section.heading}</ClauseHeading>
              {section.body.map((paragraph, paragraphIndex) => (
                <ClauseParagraph key={paragraphIndex}>{renderClauseParagraph(paragraph)}</ClauseParagraph>
              ))}
            </div>
          ))}
        </ClausesSection>
      </ContractDetailsContent>

      <EditContractModal
        isOpen={showEditModal}
        contract={contract}
        onClose={() => setShowEditModal(false)}
        onSaved={setContract}
      />

      <Modal isOpen={showMissingVarsConfirm} onClose={() => setShowMissingVarsConfirm(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', margin: 0 }}>
            {t("contracts.contractDetail.missingVarsConfirm.title")}
          </h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6, margin: 0 }}>
            <Trans
              i18nKey="contracts.contractDetail.missingVarsConfirm.message"
              count={missingRequiredVariables.length}
              values={{ count: missingRequiredVariables.length }}
              components={{ 1: <strong /> }}
            />
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
            <Button
              onClick={async () => {
                setShowMissingVarsConfirm(false);
                await runDownload();
              }}
              style={{ width: '100%' }}
            >
              {t("contracts.contractDetail.missingVarsConfirm.downloadAnywayButton")}
            </Button>
            <p
              onClick={() => setShowMissingVarsConfirm(false)}
              style={{ textAlign: 'center', color: '#6B7280', cursor: 'pointer', margin: 0, padding: '0.5rem 0' }}
            >
              {t("contracts.contractDetail.missingVarsConfirm.cancelButton")}
            </p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showArchiveConfirm} onClose={() => setShowArchiveConfirm(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', margin: 0 }}>
            {contract.status === CONTRACT_STATUS_ARCHIVED
              ? t("contracts.contractDetail.archiveConfirm.restoreTitle")
              : t("contracts.contractDetail.archiveConfirm.archiveTitle")}
          </h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6, margin: 0 }}>
            {contract.status === CONTRACT_STATUS_ARCHIVED
              ? t("contracts.contractDetail.archiveConfirm.restoreMessage")
              : t("contracts.contractDetail.archiveConfirm.archiveMessage")}
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
            <Button
              onClick={handleArchiveToggle}
              style={contract.status === CONTRACT_STATUS_ARCHIVED ? { width: '100%' } : { backgroundColor: '#ef4444', color: '#ffffff', width: '100%' }}
            >
              {contract.status === CONTRACT_STATUS_ARCHIVED
                ? t("contracts.contractDetail.archiveConfirm.restoreButton")
                : t("contracts.contractDetail.archiveConfirm.archiveButton")}
            </Button>
            <p
              onClick={() => setShowArchiveConfirm(false)}
              style={{ textAlign: 'center', color: '#6B7280', cursor: 'pointer', margin: 0, padding: '0.5rem 0' }}
            >
              {t("contracts.contractDetail.archiveConfirm.cancelButton")}
            </p>
          </div>
        </div>
      </Modal>
    </ContractDetailsWrapper>
  );
};
